/**
 * Date-range presets for list filters (HQ #370).
 *
 * One helper, so a list's "Added in the last 30 days" means what the next
 * list's does. Before this, two pages each hand-built their own (orders'
 * `rangeForPreset`, audit's raw date inputs); this is the shared one the list
 * kit uses. Those two can move onto it; nothing forces them to.
 *
 * THE VALUE IS A STRING, so it can live in the URL and in a saved view: a
 * preset id (`last30`), or a custom range `2026-09-01..2026-09-23` with either
 * end optional. `rangeFor` turns it into the instants a query filters on, in
 * the VIEWER's calendar: the start of the first day and the end of the last,
 * local time. The server compares instants, so "today" means the day on the
 * wall of the person looking, not the server's UTC day.
 */

export const DATE_PRESETS = ['today', 'last7', 'last30', 'last90', 'thisMonth'] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];

export const DATE_PRESET_LABELS: Record<DatePreset, string> = {
  today: 'Today',
  last7: 'Last 7 days',
  last30: 'Last 30 days',
  last90: 'Last 90 days',
  thisMonth: 'This month',
};

const ISO = /^\d{4}-\d{2}-\d{2}$/;

/** A custom range as it is written in the URL: `from..to`, either end optional. */
export function customRange(from?: string, to?: string): string {
  return `${from ?? ''}..${to ?? ''}`;
}

/** The two ends of a custom range value, or `null` when it is not one. */
export function parseCustomRange(value: string): { from?: string; to?: string } | null {
  const i = value.indexOf('..');
  if (i < 0) return null;
  const from = value.slice(0, i);
  const to = value.slice(i + 2);
  if ((from && !ISO.test(from)) || (to && !ISO.test(to))) return null;
  if (!from && !to) return null;
  return { ...(from ? { from } : {}), ...(to ? { to } : {}) };
}

/** True for a value `rangeFor` understands. */
export function isDateRangeValue(value: string): boolean {
  return (DATE_PRESETS as readonly string[]).includes(value) || parseCustomRange(value) !== null;
}

const startOfDay = (d: Date): Date => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const endOfDay = (d: Date): Date =>
  new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
const localDate = (iso: string): Date => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};
const daysAgo = (now: Date, n: number): Date =>
  new Date(now.getFullYear(), now.getMonth(), now.getDate() - n);

/** The instants a range value covers, in the viewer's local calendar. */
export function rangeFor(value: string, now: Date = new Date()): { from?: Date; to?: Date } {
  switch (value) {
    case 'today':
      return { from: startOfDay(now), to: endOfDay(now) };
    case 'last7':
      return { from: daysAgo(now, 6), to: endOfDay(now) };
    case 'last30':
      return { from: daysAgo(now, 29), to: endOfDay(now) };
    case 'last90':
      return { from: daysAgo(now, 89), to: endOfDay(now) };
    case 'thisMonth':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: endOfDay(now) };
    default: {
      const custom = parseCustomRange(value);
      if (!custom) return {};
      return {
        ...(custom.from ? { from: startOfDay(localDate(custom.from)) } : {}),
        ...(custom.to ? { to: endOfDay(localDate(custom.to)) } : {}),
      };
    }
  }
}

/** The words `describeRange` uses; English unless a caller passes its own (#714). */
export interface DateRangeWords {
  presets: Record<DatePreset, string>;
  from: (date: string) => string;
  until: (date: string) => string;
}

export const DATE_RANGE_WORDS_EN: DateRangeWords = {
  presets: DATE_PRESET_LABELS,
  from: (date) => `from ${date}`,
  until: (date) => `until ${date}`,
};

/** How a range value reads in a filter chip: "Last 30 days", "1 Sep – 23 Sep". */
export function describeRange(value: string, words: DateRangeWords = DATE_RANGE_WORDS_EN): string {
  if ((DATE_PRESETS as readonly string[]).includes(value)) {
    return words.presets[value as DatePreset];
  }
  const custom = parseCustomRange(value);
  if (!custom) return value;
  if (custom.from && custom.to) return `${custom.from} – ${custom.to}`;
  return custom.from ? words.from(custom.from) : words.until(custom.to ?? '');
}
